"use client";

import { ArrowRight, Mail, MapPin, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const statusIndicators = [{
  label: "CONSULTING SLOTS OPEN"
}, {
  label: "REVENUE AUDIT READY"
}, {
  label: "CONFIDENTIALITY ASSURED"
}];
const contactDetails = [{
  label: "GENERAL INQUIRIES",
  value: "hello@hotelrevbaba.com",
  href: "mailto:hello@hotelrevbaba.com"
}, {
  label: "BOOK A CONSULTATION",
  value: "book@hotelrevbaba.com",
  href: "mailto:book@hotelrevbaba.com"
}, {
  label: "PARTNERSHIPS",
  value: "partners@hotelrevbaba.com",
  href: "mailto:partners@hotelrevbaba.com"
}, {
  label: "SUPPORT",
  value: "support@hotelrevbaba.com",
  href: "mailto:support@hotelrevbaba.com"
}];
const socials = [{
  label: "INSTAGRAM",
  href: "#"
}, {
  label: "TWITTER",
  href: "#"
}, {
  label: "FACEBOOK",
  href: "#"
}, {
  label: "LINKEDIN",
  href: "#"
}];
const projectTypes = [{
  value: "ota",
  label: "OTA Strategy"
}, {
  value: "tech",
  label: "Tech Stack"
}, {
  value: "audit",
  label: "Revenue Audit"
}, {
  value: "all",
  label: "Full Revenue Growth"
}];
const budgetRanges = [{
  value: "under-5k",
  label: "Under $5k"
}, {
  value: "5-15k",
  label: "$5k – $15k"
}, {
  value: "15-30k",
  label: "$15k – $30k"
}, {
  value: "30-plus",
  label: "$30k+"
}];
const timelines = ["Immediate — Within 2 weeks", "1 month", "3 months", "6 months", "Planning phase"];
function IgniteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="relative overflow-hidden border border-border bg-card p-10 md:p-14 rounded-lg">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,var(--secondary)/0.15,transparent_60%)] pointer-events-none" />
        <p className="font-default text-xs tracking-[0.2em] text-secondary mb-6">INQUIRY RECEIVED</p>
        <h3 className="font-default text-4xl md:text-5xl font-bold leading-tight text-foreground mb-6">
          {successMessage}
        </h3>
        <p className="text-muted-foreground mb-8 max-w-lg">
          Sunil Singh will review your property details and reach out within 24 hours to discuss your revenue potential.
        </p>
        <Button onClick={resetForm} className="btn-primary rounded-lg">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <div className="relative border border-border bg-card/40 backdrop-blur-sm p-8 md:p-12 lg:p-14 shadow-lg rounded-lg">
      <div className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-secondary/40 to-transparent" />

      <Form onSubmit={handleSubmit} className="space-y-12">
        <div className="space-y-8">
          <div className="flex items-baseline gap-4 border-b border-border pb-3">
            <span className="font-default text-xs tracking-[0.25em] text-secondary">01.</span>
            <span className="font-default text-xs tracking-[0.25em] text-muted-foreground">PROPERTY DETAILS</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <FormField control={control} name="name" rules={{
            required: "Your name is required"
          }} render={({
            field
          }) => <FormItem className="gap-3">
                  <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                    Full Name *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Sunil Singh" className="h-12 border-0 border-b border-border bg-transparent px-0 text-lg font-default focus-visible:ring-0 focus-visible:border-secondary placeholder:text-muted-foreground/40" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem className="gap-3">
                  <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                    Email Address *
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="hotelier@example.com" className="h-12 border-0 border-b border-border bg-transparent px-0 text-lg font-default focus-visible:ring-0 focus-visible:border-secondary placeholder:text-muted-foreground/40" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" render={({
            field
          }) => <FormItem className="gap-3 md:col-span-2">
                  <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                    Property Location / Phone
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+91 98765 43210" className="h-12 border-0 border-b border-border bg-transparent px-0 text-lg font-default focus-visible:ring-0 focus-visible:border-secondary placeholder:text-muted-foreground/40" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <div className="space-y-8">
          <div className="flex items-baseline gap-4 border-b border-border pb-3">
            <span className="font-default text-xs tracking-[0.25em] text-secondary">02.</span>
            <span className="font-default text-xs tracking-[0.25em] text-muted-foreground">SERVICE FOCUS</span>
          </div>

          <FormField control={control} name="service_interest" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem className="gap-4">
                <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                  Primary Goal *
                </FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-wrap gap-3">
                    {projectTypes.map((type, i) => <label key={type.value} data-index={i} htmlFor={`pt-${type.value}`} className="cursor-pointer">
                        <RadioGroupItem value={type.value} id={`pt-${type.value}`} className="sr-only peer" />
                        <span className="inline-flex items-center px-5 py-2.5 rounded-lg border border-border bg-transparent text-sm font-default tracking-wide text-foreground transition-all hover:border-secondary/60 hover:text-secondary peer-data-[state=checked]:bg-secondary peer-data-[state=checked]:text-white peer-data-[state=checked]:border-secondary">
                          {type.label}
                        </span>
                      </label>)}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem className="gap-4">
                <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                  Monthly Revenue Target
                </FormLabel>
                <FormControl>
                  <ToggleGroup type="single" value={field.value} onValueChange={val => val && field.onChange(val)} className="flex flex-wrap gap-0 w-full">
                    {budgetRanges.map((b, i) => <ToggleGroupItem key={b.value} data-index={i} value={b.value} className="flex-1 min-w-[140px] h-12 font-default text-xs tracking-wider border-border data-[state=on]:bg-secondary data-[state=on]:text-white">
                        {b.label}
                      </ToggleGroupItem>)}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="timeline" render={({
          field
        }) => <FormItem className="gap-3">
                <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                  Implementation Timeline
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="h-12 border-0 border-b border-border bg-transparent px-0 text-base font-default focus-visible:ring-0 w-full">
                      <SelectValue placeholder="When are you looking to start?" />
                    </SelectTrigger>
                    <SelectContent>
                      {timelines.map((t, i) => <SelectItem key={t} data-index={i} value={t}>
                          {t}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="space-y-8">
          <div className="flex items-baseline gap-4 border-b border-border pb-3">
            <span className="font-default text-xs tracking-[0.25em] text-secondary">03.</span>
            <span className="font-default text-xs tracking-[0.25em] text-muted-foreground">PROPERTY CHALLENGES</span>
          </div>

          <FormField control={control} name="message" rules={{
          required: "Please tell us about your property",
          minLength: {
            value: 10,
            message: "Please provide a bit more detail"
          }
        }} render={({
          field
        }) => <FormItem className="gap-3">
                <FormLabel className="font-default text-xs tracking-[0.2em] text-muted-foreground uppercase">
                  Describe your current revenue challenges *
                </FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us about your occupancy rates, OTA performance, or tech stack issues..." rows={6} className="border-0 border-b border-border bg-transparent px-0 py-3 text-base font-default focus-visible:ring-0 focus-visible:border-secondary placeholder:text-secondary/30 resize-none" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
            <p className="text-destructive text-sm font-default">{submitError}</p>
          </div>}

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="btn-primary group relative h-16 px-10 rounded-lg shadow-lg hover:shadow-xl transition-all">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Processing...
              </> : <>
                Submit Inquiry
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </>}
          </Button>
          <p className="mt-5 font-default text-xs tracking-wider text-muted-foreground">
            or email us directly at{" "}
            <Link to="mailto:hello@hotelrevbaba.com" className="text-secondary hover:underline underline-offset-4">
              hello@hotelrevbaba.com
            </Link>
          </p>
        </div>
      </Form>
    </div>;
}
export default function RevenueJourneyLeadForm() {
  const heading = useRevealOnScroll<HTMLDivElement>();
  const details = useRevealOnScroll<HTMLDivElement>();
  const formReveal = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3723" id="revenuejourneyleadform" className="relative bg-background text-foreground overflow-hidden pt-[180px] pb-32 px-6 md:px-12 lg:px-20">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(circle,var(--secondary)/0.08,transparent_70%)] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[radial-gradient(circle,var(--secondary)/0.05,transparent_70%)] pointer-events-none" />

      <div className="relative max-w-[1500px] mx-auto">
        <div ref={heading.ref} className={`transition-all duration-1000 ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default font-bold leading-[0.95] tracking-tight text-foreground" style={{
          fontSize: "clamp(3rem, 8vw, 6rem)"
        }}>
            Maximize your
            <br />
            <span className="italic text-secondary">hotel revenue</span>
            <br />
            with expert guidance.
          </h2>
        </div>

        <div className={`mt-16 flex flex-wrap items-center gap-x-10 gap-y-4 border-y border-border py-5 transition-all duration-1000 delay-150 ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {statusIndicators.map((s, i) => <div data-index={i} key={s.label} className="flex items-center gap-3">
              <span className="relative flex size-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-secondary opacity-75" />
                <span className="relative inline-flex rounded-lg size-2 bg-secondary" />
              </span>
              <span className="font-default text-xs tracking-[0.2em] text-secondary">{s.label}</span>
            </div>)}
        </div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={details.ref} className={`lg:col-span-4 space-y-12 transition-all duration-1000 ${details.isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="space-y-8">
              {contactDetails.map((c, i) => <div data-index={i} key={c.label} className="group">
                  <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-2">
                    {c.label}
                  </p>
                  <Link to={c.href} className="font-default text-xl md:text-2xl text-foreground group-hover:text-secondary transition-colors relative inline-block">
                    <span className="border-b border-transparent group-hover:border-secondary transition-colors pb-1">
                      {c.value}
                    </span>
                  </Link>
                </div>)}
            </div>

            <div className="space-y-6 pt-8 border-t border-border">
              <div className="flex items-start gap-4">
                <Phone className="size-4 text-secondary mt-1 shrink-0" />
                <div>
                  <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-1">PHONE</p>
                  <p className="font-default text-lg text-foreground">+91 98765 43210</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="size-4 text-secondary mt-1 shrink-0" />
                <div>
                  <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-1">OFFICE</p>
                  <p className="font-default text-lg text-foreground leading-snug">
                    Revenue Management Hub
                    <br />
                    New Delhi, India
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="size-4 text-secondary mt-1 shrink-0" />
                <div>
                  <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-1">HOURS</p>
                  <p className="font-default text-lg text-foreground">Mon–Sat · 9am–6pm IST</p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-5">FOLLOW</p>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {socials.map((s, i) => <Link data-index={i} key={s.label} to={s.href} className="font-default text-xs tracking-[0.2em] text-foreground hover:text-secondary transition-colors border-b border-transparent hover:border-secondary pb-0.5">
                    {s.label}
                  </Link>)}
              </div>
            </div>
          </div>

          <div ref={formReveal.ref} className={`lg:col-span-8 transition-all duration-1000 delay-200 ${formReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="revenue_journey_form_contact" formId="revenue_journey_form_contact" sectionName="revenuejourneyleadform" defaultValues={{
            name: "",
            email: "",
            phone: "",
            service_interest: "",
            budget: "",
            timeline: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            service_interest: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Inquiry" successMessage="Thank you — your revenue journey begins now." errorMessage="Something went wrong. Please try again or email us directly.">
              <IgniteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}